import { Box, TextInput, Select, SimpleGrid, Text, Flex, Button, FileButton, Image, Tooltip } from "@mantine/core";
import { IconBuilding, IconLock, IconInfoCircle } from "@tabler/icons-react";
import { countriesData } from "../../../components/shared/constants/general.const";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { useState, useEffect, useRef } from "react";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { errorNotification } from "@/shared/utils/errorNotification";

interface BasicCompanyInfoProps {
  formData: {
    companyLegalName: string;
    tradeName: string;
    headquartersCountry: string;
    registrationNumber: string;
    registrationIdType: string | null;
    companyWebsite: string;
    companyEmail: string;
    phoneNumber: string;
    phoneCountryCode: string;
  };
  updateForm: (field: string, value: any) => void;
  logo: File | null;
  setLogo: (file: File | null) => void;
  countryOptions: { group: string; items: string[] }[];
  existingLogo?: any;
  onRemoveLogo?: () => void;
  isFieldLocked?: (fieldName: string) => boolean;
}

export default function BasicCompanyInfo({
  formData,
  updateForm,
  logo,
  setLogo,
  countryOptions,
  existingLogo,
  onRemoveLogo,
  isFieldLocked = () => false,
}: BasicCompanyInfoProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<() => void>(null);

  const lockedTooltip = "This field cannot be edited after verification";

  useEffect(() => {
    let mounted = true;

    const loadLogoPreview = async () => {
      if (logo) {
        const preview = URL.createObjectURL(logo);
        if (mounted) setLogoPreview(preview);
        return () => URL.revokeObjectURL(preview);
      } else if (existingLogo?.id) {
        try {
          const url = await getFileUrl(existingLogo.id);
          if (mounted) setLogoPreview(url);
        } catch (err) {
          console.error("Failed to load logo preview:", err);
          if (mounted) setLogoPreview(null);
        }
      } else {
        if (mounted) setLogoPreview(null);
      }
    };

    loadLogoPreview();

    return () => {
      mounted = false;
    };
  }, [logo, existingLogo]);

  /* ---------------- Registration ID Logic ---------------- */
  const isIndia = formData.headquartersCountry === "India";

  const getRegistrationIdType = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India" && selectedType) {
      return selectedType; // GST, CIN, UDM
    }
    if (!headquartersCountry) return "Registration Number";
    for (const group of countriesData.countryGroups) {
      const countryItem = group.items.find((item) => item.name === headquartersCountry);
      if (countryItem) {
        return countryItem.registrationName;
      }
    }
    return "Registration Number";
  };

  const getRegistrationPlaceholder = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India") {
      return selectedType ? `Enter ${selectedType}` : "Select Registration Type first";
    }
    return `Enter ${getRegistrationIdType(headquartersCountry)}`;
  };

  // Helper to render label with optional lock icon
  const renderLabel = (label: string, fieldName: string, required = false, extra?: React.ReactNode) => (
    <Flex align="center" gap={4}>
      <Text fz="sm" fw={500}>
        {label}
        {required && (
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        )}
        {extra}
      </Text>
      {isFieldLocked(fieldName) && (
        <Tooltip label={lockedTooltip} withArrow position="top">
          <Flex align="center">
            <IconLock size={12} color="#9CA3AF" />
          </Flex>
        </Tooltip>
      )}
    </Flex>
  );

  return (
    <Box>
      {/* Company Logo - Always editable */}
      <Text fz="sm" fw={500} mb="xs">
        Company Logo
      </Text>
      <Flex gap="md" align="center" mb="xl">
        <Box
          style={{
            width: 96,
            height: 96,
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F8FAFC",
          }}
        >
          {logoPreview ? (
            <Image
              src={logoPreview}
              alt="Company Logo"
              fit="cover"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                console.error("❌ Logo failed to load:", logoPreview);
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <IconBuilding size={28} color="#94A3B8" />
          )}
        </Box>
        <Box>
          <Flex gap="xs">
            <FileButton
              resetRef={logoInputRef}
              onChange={(file) => {
                if (file) {
                  const maxSize = 2 * 1024 * 1024; // 2MB
                  if (file.size > maxSize) {
                    errorNotification({
                      title: "File too large",
                      message: "Logo size must be less than 2MB",
                    });
                    logoInputRef.current?.();
                    return;
                  }
                  setLogo(file);
                  logoInputRef.current?.();
                }
              }}
              accept="image/png,image/jpeg"
            >
              {(props) => (
                <Button variant="outline" size="xs" {...props}>
                  Upload Logo
                </Button>
              )}
            </FileButton>

            {(logo || logoPreview) && (
              <Button
                variant="outline"
                size="xs"
                color="red"
                onClick={() => {
                  if (onRemoveLogo) {
                    onRemoveLogo();
                  } else {
                    setLogo(null);
                    setLogoPreview(null);
                  }
                }}
              >
                Remove
              </Button>
            )}
          </Flex>
          <Text fz="xs" c="slate.6" mt={6}>
            PNG, JPG up to 2MB. Recommended: 400×400px square logo
          </Text>
        </Box>
      </Flex>

      {/* Company Legal Name - LOCKED after verification */}
      <TextInput
        label={renderLabel("Company Legal Name", "companyLegalName", true)}
        placeholder="Enter registered company name"
        value={formData.companyLegalName}
        onChange={(e) => updateForm("companyLegalName", e.currentTarget.value)}
        mb="md"
        disabled={isFieldLocked("companyLegalName")}
      />

      {/* Trade Name / DBA - Always editable */}
      <TextInput
        label={
          <Text fz="sm" fw={500}>
            Trade Name / DBA{" "}
          </Text>
        }
        placeholder="Public brand name"
        value={formData.tradeName}
        onChange={(e) => updateForm("tradeName", e.currentTarget.value)}
        mb="md"
      />

      {/* Country - LOCKED after verification */}
      <Select
        label={renderLabel("Country", "headquartersCountry", true)}
        placeholder="Select country"
        data={countryOptions}
        value={formData.headquartersCountry}
        onChange={(value) => {
          updateForm("headquartersCountry", value);
          updateForm("country", value);
        }}
        searchable
        mb="xs"
        disabled={isFieldLocked("headquartersCountry")}
      />
      <Flex align="center" mb="md">
        <IconInfoCircle size={14} color="var(--mantine-color-primary-7)" />
        <Text fz="xs" c="primary" ml={2}>
          Registration identifier is auto-selected based on country:{" "}
          {formData.headquartersCountry
            ? isIndia
              ? "Select Type (GST/CIN/UDM)"
              : getRegistrationIdType(formData.headquartersCountry)
            : "VAT / Business ID"}
        </Text>
      </Flex>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        {isIndia && (
          <Select
            label={renderLabel("Registration ID Type", "registrationIdType", true)}
            placeholder="Select Type"
            data={[
              { value: "GST", label: "GST" },
              { value: "CIN", label: "CIN" },
              { value: "UDM", label: "UDM (Udyam)" },
            ]}
            value={formData.registrationIdType}
            onChange={(value) => {
              updateForm("registrationIdType", value);
              // Clear previous number if type changes to prevent mismatch? - Optional, maybe better to keep
            }}
            disabled={isFieldLocked("registrationIdType")}
          />
        )}

        {/* Registration Number - LOCKED after verification */}
        <TextInput
          label={renderLabel(
            getRegistrationIdType(formData.headquartersCountry, formData.registrationIdType ?? undefined),
            "registrationNumber",
            true
          )}
          placeholder={getRegistrationPlaceholder(formData.headquartersCountry, formData.registrationIdType ?? undefined)}
          value={formData.registrationNumber}
          onChange={(e) => updateForm("registrationNumber", e.currentTarget.value)}
          disabled={isFieldLocked("registrationNumber") || (isIndia && !formData.registrationIdType)}
        />
      </SimpleGrid>

      {/* Company Website - Always editable */}
      <Box mb="md">
        <Text fz="sm" fw={500} mb={4}>
          Company Website
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        </Text>
        <TextInput
          placeholder="https://www.company.com"
          value={formData.companyWebsite}
          onChange={(e) => updateForm("companyWebsite", e.currentTarget.value)}
          error={
            formData.companyWebsite &&
            !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.companyWebsite)
              ? "Please enter a valid website URL"
              : null
          }
        />
        <Text fz="xs" c="slate.6" mt={4}>
          Enter your official company website URL
        </Text>
      </Box>

      {/* Company Email & Phone Number */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {/* Company Email - Always editable */}
        <TextInput
          label={renderLabel("Company Email", "companyEmail", true)}
          placeholder="contact@company.com"
          value={formData.companyEmail}
          onChange={(e) => updateForm("companyEmail", e.currentTarget.value)}
          error={
            formData.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)
              ? "Please enter a valid email address"
              : null
          }
          disabled={isFieldLocked("companyEmail") || true}
        />

        {/* Phone Number - Always editable */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Phone Number
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          placeholder="hidden"
          styles={{
            input: {
              display: "none",
            },
            wrapper: {
              display: "none",
            },
            root: {
              marginBottom: 0,
            },
          }}
          inputContainer={() => (
            <PhoneInput
              defaultCountry="in"
              value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                updateForm("phoneCountryCode", countryCode);
                updateForm("phoneNumber", nationalNumber);
              }}
              placeholder="123-456-7890"
              style={{
                width: "100%",
              }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
              }}
            />
          )}
        />
      </SimpleGrid>
    </Box>
  );
}
